import React, { useState, useEffect, useCallback } from "react";
import "./UserCalendarScreen.css";
import { useLocation, Link } from "react-router-dom";
import Calendar from "react-calendar";
import { API_IP } from "../global_variables";
import { axiosReqConfig } from "../functions/axiosRequest";
import Modal from "./UserCalendarScreen/CalendarMainModal";
import PromptModal from "./ReuseComponent/PromptModal";

const UserCalendarScreen = ({ setSelectedSection }) => {
  const username = localStorage.getItem("username");
  const [isRefresh, setIsRefresh] = useState(false);
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [eventDetail, setEventDetail] = useState([]);
  const [eventStatusStyle, setEventStatusStyle] = useState("");
  const [eventStatus, setEventStatus] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [patientData, setPatientData] = useState([]);
  const [previousEvent, setPreviousEvent] = useState(null);
  const [isPromptVisible, setIsPromptVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [alertContent, setAlertContent] = useState("");

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  const getEventForCalendars = useCallback(async () => {
    const data = await axiosReqConfig(`${API_IP}/api/calendars`, "get", []);
    setEvents(data);
  }, [isRefresh]);

  const cancelAppointment = async () => {
    try {
      setIsLoading(true);
      await axiosReqConfig(
        `${API_IP}/api/calendars/${eventDetail.fk_booking_id}`,
        "delete",
        { date: eventDetail.date_day },
      );
      setEventDetail([]);
      toggleIsRefresh();
      setTimeout(() => {
        setIsLoading(false);
        togglePrompt();
      }, 2000);
    } catch (error) {
      alert(error);
    }
  };

  const toggleAlert = () => {
    setIsAlertVisible(!isAlertVisible);
  };

  const togglePrompt = () => {
    if (isPromptVisible === false && eventStatus === "Accepted") {
      setAlertContent(
        "You can only cancel an appointment that has not been accepted or still pending by any provider!",
      );
      setIsAlertVisible(true);
    } else if (eventDetail.length === 0) {
      setAlertContent("Please select a pending appointment");
      setIsAlertVisible(true);
    } else {
      setIsPromptVisible(!isPromptVisible);
    }
  };

  const toggleIsRefresh = () => {
    setIsRefresh(!isRefresh);
  };

  const selectEvent = (selectedEvent, index, event_style, event_status) => {
    if (previousEvent !== null) {
      previousEvent.selected = false;
    }
    setPreviousEvent(selectedEvent);
    selectedEvent.selected = true;
    setEventDetail(selectedEvent);
    setEventStatusStyle(event_style);
    setEventStatus(event_status);
  };

  const timeList = [
    "7:00",
    "8:00",
    "9:00",
    "10:00",
    "11:00",
    "12:00",
    "13:00",
    "14:00",
    "15:00",
    "16:00",
    "17:00",
    "18:00",
    "19:00",
    "20:00",
    "21:00",
    "22:00",
  ];

  const formatDate = (date) => {
    const options = { month: "long", day: "numeric", year: "numeric" };
    return new Intl.DateTimeFormat("en-US", options).format(date);
  };

  const getStartOfWeek = (date) => {
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(startOfWeek.setDate(diff));
  };

  const changeWeek = (days) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(currentWeekStart.getDate() + days);
    setCurrentWeekStart(getStartOfWeek(newDate));
  };

  const onDateSelect = (date) => {
    setCurrentWeekStart(getStartOfWeek(date));
    setIsCalendarVisible(false);
  };

  const weekStart = getStartOfWeek(currentWeekStart);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  const daysOfWeek = [];
  for (let i = 0; i < 7; i++) {
    const currentDay = new Date(weekStart);
    currentDay.setDate(weekStart.getDate() + i);
    daysOfWeek.push(currentDay);
  }

  // FIXED: Logic tính toán vị trí Event với độ chính xác theo phút
  const getEventPosition = (startTime, endTime) => {
    if (!startTime || !endTime) return { top: "0px", height: "0px" };

    const parseTime = (timeStr) => {
      const [hourStr, minStr] = timeStr.split(":");
      return parseInt(hourStr, 10) + parseInt(minStr || 0, 10) / 60;
    };

    const start = parseTime(startTime);
    const end = parseTime(endTime);
    const duration = end - start;

    const rowHeight = 110;
    const headerHeight = 72;

    // Bắt đầu từ mốc 7:00
    const startPosition = (start - 7) * rowHeight + headerHeight;

    return {
      top: `${startPosition}px`,
      height: `${duration * rowHeight}px`,
    };
  };

  function stripTime(date_day) {
    if (typeof date_day === "string") {
      const date = new Date(date_day);
      return new Date(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
      );
    }
    const date = new Date(date_day);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  const getPatientData = async () => {
    const data = await axiosReqConfig(`${API_IP}/api/patients`, "get", []);
    setPatientData(data);
  };

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      getEventForCalendars();
      getPatientData();
    }
    return () => {
      isMounted = false;
    };
  }, [getEventForCalendars]);

  return (
    <>
      <Modal
        showModal={showModal}
        handleClose={closeModal}
        patientData={patientData}
        toggleRefresh={toggleIsRefresh}
        setSelectedSection={setSelectedSection}
      />
      <PromptModal
        title="Do you want to cancel this appointment?"
        type="binary"
        showModal={isPromptVisible}
        handleClose={togglePrompt}
        handleConsent={cancelAppointment}
        isLoading={isLoading}
      />
      <PromptModal
        title={alertContent}
        type="alert"
        showModal={isAlertVisible}
        handleClose={toggleAlert}
        isLoading={isLoading}
      />
      <div className="screen-container">
        <div className="screen-header">
          <div className="screen-header-text">
            <h1 className="screen-title">Calendar</h1>
            <p className="screen-subtitle">
              Manage your schedule and upcoming appointments.
            </p>
          </div>
        </div>

        <div className="UCS-main-wrapper">
          <div className="UCS-calendar-side">
          <div className="UCS-toolbar">
            <div className="UCS-date-nav">
              <button className="UCS-nav-btn" onClick={() => changeWeek(-7)}>
                &#9664;
              </button>
              <span
                className="UCS-date-label"
                onClick={() => setIsCalendarVisible(!isCalendarVisible)}
              >
                {`${formatDate(weekStart)} - ${formatDate(weekEnd)}`}
              </span>
              <button className="UCS-nav-btn" onClick={() => changeWeek(7)}>
                &#9654;
              </button>

              {isCalendarVisible && (
                <div className="UCS-picker-dropdown">
                  <Calendar
                    onClickDay={onDateSelect}
                    value={currentWeekStart}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="UCS-note-banner">
            <p>
              View your upcoming care visits or book a new one. If your calendar
              is empty, no appointments have been scheduled yet.
            </p>
          </div>

          <div className="UCS-calendar-showcase">
            <div className="UCS-calendar-grid">
              <div className="UCS-time-col">
                <div className="UCS-time-placeholder"></div>
                {timeList.map((time, index) => (
                  <div key={index} className="UCS-time-slot">
                    <span>{time}</span>
                  </div>
                ))}
              </div>

              <div className="UCS-day-cols">
                {daysOfWeek.map((date, index) => (
                  <div key={index} className="UCS-day-col">
                    <div className="UCS-day-label">
                      <span className="UCS-weekday">
                        {date.toLocaleDateString("en-US", { weekday: "short" })}
                      </span>
                      <span className="UCS-daynum">
                        {date.toLocaleDateString("en-US", { day: "numeric" })}
                      </span>
                    </div>

                    {/* Lưới ngang hiển thị dưới background */}
                    <div className="UCS-day-bg">
                      {timeList.map((_, idx) => (
                        <div key={idx} className="UCS-bg-slot"></div>
                      ))}
                    </div>

                    {events
                      .filter(
                        (event) =>
                          stripTime(date).getTime() ===
                          stripTime(event.date_day).getTime(),
                      )
                      .map((event, eventIndex) => {
                        const style = getEventPosition(
                          event.start_time,
                          event.end_time,
                        );
                        let event_style;
                        let event_status;

                        switch (event.booking_acceptance) {
                          case true:
                            if (event.date_acceptance) {
                              event_style = "UCS-event-accepted";
                              event_status = "Accepted";
                            } else {
                              event_style = "UCS-event-denied";
                              event_status = "Denied";
                            }
                            break;
                          case null:
                            event_style = "UCS-event-pending";
                            event_status = "Pending";
                            break;
                          case false:
                            event_style = "UCS-event-denied";
                            event_status = "Denied";
                            break;
                          default:
                            event_style = "UCS-event-pending";
                            event_status = "Pending";
                            break;
                        }

                        return (
                          <div
                            key={eventIndex}
                            className={`UCS-event ${event_style} ${event.selected ? "UCS-event-selected" : ""}`}
                            onClick={() =>
                              selectEvent(
                                event,
                                eventIndex,
                                event_style,
                                event_status,
                              )
                            }
                            style={style}
                          >
                            <span className="UCS-event-provider">
                              {event.provider_name}
                            </span>
                            <span className="UCS-event-time">
                              {event.start_time} - {event.end_time}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="UCS-details-side">
          <div className="UCS-details-header">
            <h2>Appointment Details</h2>
          </div>

          <div className="UCS-details-card">
            {eventDetail.length === 0 ? (
              <div className="UCS-details-empty">
                <img
                  src={require("../images/calendar_icon.png")}
                  alt="Calendar"
                  className="UCS-empty-icon"
                />
                <p className="UCS-details-text">
                  You don’t have any appointments scheduled yet, or none are
                  selected.
                </p>
                <p className="UCS-details-text">
                  To get started, click <strong>“Book a Care Visit”</strong>{" "}
                  below and choose a service, date, and time.
                </p>
              </div>
            ) : (
              <div className="UCS-details-content">
                <div className="UCS-detail-group">
                  <h1 className="UCS-detail-provider">
                    {eventDetail.provider_name}
                  </h1>
                  <span className={`UCS-status-badge ${eventStatusStyle}`}>
                    {eventStatus}
                  </span>
                </div>
                <div className="UCS-detail-group">
                  <h5>Time</h5>
                  <p>
                    {eventDetail.start_time} - {eventDetail.end_time}
                  </p>
                </div>
                <div className="UCS-detail-group">
                  <h5>Service</h5>
                  <p>{eventDetail.care_service_name}</p>
                </div>
                <div className="UCS-detail-group">
                  <h5>Client</h5>
                  <p className="UCS-patient-name">
                    {eventDetail.patient_first_name}{" "}
                    {eventDetail.patient_last_name}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="UCS-actions-box">
            <button onClick={openModal} className="UCS-action-btn primary">
              Book a Care Visit
            </button>
            <button
              className={`UCS-action-btn ${eventDetail.length === 0 ? "disabled" : "danger"}`}
              onClick={togglePrompt}
            >
              Cancel Appointment
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default UserCalendarScreen;
